import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * SamePagePulse — when a visitor clicks any same-route internal link
 * (header tab, mobile menu item, logo, footer link...), the page must
 * feel "refreshed": scroll to top + briefly re-focus the main region.
 *
 * Owner directive Feb 2026: "if you're on a page and you click the URL,
 * it doesn't update the page. It just stays put. So it needs to refresh
 * and go back up. Every page should be clickable."
 *
 * Why a delegated document listener?
 *   React Router suppresses navigation when the destination matches the
 *   current location, so `<Link>` clicks become silent no-ops. Wrapping
 *   every Link/NavLink site-wide would be invasive. Instead we listen
 *   once at the document level, identify clicks on internal anchors,
 *   and intercept the no-op case.
 */
export default function SamePagePulse() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    function handler(e) {
      // Only plain left-clicks with no modifier keys (let Cmd/Ctrl/Shift
      // open new tabs naturally).
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.target.closest("a[href]");
      if (!link) return;
      // Skip targets that open in a new tab / outbound / downloads.
      const target = link.getAttribute("target");
      if (target && target !== "_self") return;
      if (link.hasAttribute("download")) return;
      const href = link.getAttribute("href") || "";
      // Skip protocol-handler anchors (mailto:, tel:, #fragment-only).
      if (href.startsWith("mailto:") || href.startsWith("tel:")) return;
      // Resolve against the current document origin to filter outbound.
      let url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      // Compare pathnames (ignore search + hash). When they match we have
      // a same-route click that React Router will silently swallow.
      const samePath = url.pathname === pathname;
      if (!samePath) return;
      const targetHash = url.hash || "";
      // Same path + an anchor hash: let the browser handle the in-page
      // jump unless it's a redundant click on the same hash.
      if (targetHash) {
        if (targetHash !== hash) return; // browser will navigate normally
        // Same hash → re-trigger scroll to the anchor element.
        e.preventDefault();
        const id = targetHash.replace("#", "");
        const el = document.getElementById(id);
        if (el) {
          const headerOffset = window.matchMedia("(min-width: 768px)").matches
            ? 72 + 64
            : 64 + 56;
          const top = el.getBoundingClientRect().top + window.scrollY - headerOffset;
          window.scrollTo({ top, behavior: "smooth" });
        }
        return;
      }
      // Pure same-path click: prevent default, scroll to top, focus main.
      // We also push an empty hash + replace it so React Router's
      // ScrollToTop / focus side-effects re-run consistently.
      e.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      requestAnimationFrame(() => {
        const main = document.getElementById("main-content");
        if (main && typeof main.focus === "function") {
          try {
            main.focus({ preventScroll: true });
          } catch {
            main.focus();
          }
        }
      });
      // Best-effort: trigger a navigate to the same path with `replace:true`
      // so any page-level useEffect listening to location ticks fires.
      try {
        navigate(url.pathname + url.search, { replace: true });
      } catch {
        /* ignore */
      }
    }

    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [pathname, hash, navigate]);

  return null;
}
